import { useEffect } from "react";

/**
 * useVisualViewportHeight — Wave 19 #95.
 *
 * On iOS Safari (esp. PWA standalone), the soft keyboard does NOT
 * resize `100dvh` reliably. The user's bottom-fixed composer ends up
 * hidden behind the keyboard.
 *
 * This hook subscribes to the VisualViewport API and writes the actual
 * visible height into a CSS custom property `--hv-vvh` on
 * `document.documentElement`. Any element that wants to fill exactly
 * the visible viewport (e.g. the chat shell) can reference
 * `min(var(--hv-vvh, 100dvh), 100dvh)` and stay pinned correctly when
 * the keyboard opens.
 *
 * Falls back to `100dvh` on browsers without VisualViewport (older
 * Android WebView, ancient Edge) — the default behaviour was already
 * "good enough" there.
 *
 * Use once, near the chat shell. Idempotent — installing it twice is
 * harmless.
 */
export default function useVisualViewportHeight() {
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => {
      // Subtract any vertical offset (page may scroll up to make room
      // for the keyboard on Android Chrome) so the shell starts where
      // the visible area begins, not at the top of the document.
      const h = Math.max(0, vv.height);
      document.documentElement.style.setProperty("--hv-vvh", `${h}px`);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      // Don't remove the var — other consumers may still rely on it.
    };
  }, []);
}
